import PropTypes from 'prop-types';

import { jobPropTypes } from 'src/components/JobForm/propTypes';
import { noop } from 'src/utils/core/event';

/* PropTypes */

export const ExpandableTabsPropTypes = {
  disableReviewNotesButton: PropTypes.bool,
  retryAutoSave: PropTypes.func,
  retrying: PropTypes.bool,
  saveFailedQuestions: PropTypes.array,
  setIsInfoCollapsed: PropTypes.func,
  setShowHistory: PropTypes.func,
  setShowNote: PropTypes.func,
  setShowResult: PropTypes.func,
};

export const InfoOverlayPropTypes = {
  activeQuestion: PropTypes.object,
  activeReportReviewComments: PropTypes.array,
  activeReportReviewNote: PropTypes.object,
  addNote: PropTypes.func,
  datasheetCurrentVersionId: PropTypes.number,
  datasheetId: PropTypes.number,
  forExternalUsersOnly: PropTypes.bool,
  getResult: PropTypes.func,
  getResultAttachments: PropTypes.func,
  getResultExternalAttachments: PropTypes.func,
  hasSaqService: PropTypes.bool,
  hasUnverifiedSaqService: PropTypes.bool,
  isCreateNote: PropTypes.bool,
  isInfoCollapsed: PropTypes.bool,
  isSidebarExpanded: PropTypes.bool,
  isUnderReviewing: PropTypes.bool,
  job: jobPropTypes,
  onDeletingExternalAttachment: PropTypes.func,
  readonly: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  refetchReportReview: PropTypes.func,
  resultHistories: PropTypes.array,
  setIsCreateNote: PropTypes.func,
  setIsInfoCollapsed: PropTypes.func,
  setResult: PropTypes.func,
  setShowNote: PropTypes.func,
  setShowResult: PropTypes.func,
  showHistory: PropTypes.bool,
  showNote: PropTypes.bool,
  showResult: PropTypes.bool,
  userIsAuthorizedExternalSaqUser: PropTypes.bool,
};

export const FormPropTypes = {
  datasheet: PropTypes.object,
  getResultHistories: PropTypes.func,
  hasSaqService: PropTypes.bool,
  hasUnverifiedSaqService: PropTypes.bool,
  isDAPVisible: PropTypes.bool,
  isUnderReviewing: PropTypes.bool,
  job: jobPropTypes,
  onSave: PropTypes.func,
  readonly: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  refetchReportReview: PropTypes.func,
  refetchResultHistories: PropTypes.func,
  reportReviewData: PropTypes.object,
  reportReviewStatistics: PropTypes.arrayOf(PropTypes.object),
  resultHistories: PropTypes.object,
  results: PropTypes.array,
  storedResult: PropTypes.object,
  setResult: PropTypes.func,
  setValidationErrors: PropTypes.func,
  userIsAuthorizedExternalSaqUser: PropTypes.bool,
  isQuesPresentInMultipleDatasheets: PropTypes.func
};

export const FormSectionPropTypes = {
  applyQuestionConditions: PropTypes.func,
  collapsedSections: PropTypes.array,
  currentFilter: PropTypes.string,
  getResult: PropTypes.func,
  getValidationErrors: PropTypes.func,
  getNumberOfAttachments: PropTypes.func,
  indexPath: PropTypes.arrayOf(PropTypes.number),
  isAdditionalReportingDocuments: PropTypes.bool,
  isBlockSaveFailed: PropTypes.func,
  isBlockSaveSuccess: PropTypes.func,
  job: PropTypes.object,
  readonly: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  section: PropTypes.object,
  sectionsOptions: PropTypes.array,
  selectedQuestion: PropTypes.object,
  setResult: PropTypes.func,
  toggleSection: PropTypes.func,
  toggleSelectedQuestion: PropTypes.func,
  wrapperRef: PropTypes.object,
  isInternalUser: PropTypes.bool,
  allSections: PropTypes.array,
  isQuesPresentInMultipleDatasheets: PropTypes.func
};

export const ResultFamilySectionPropTypes = {
  activeQuestion: PropTypes.object,
  activeReportReviewComments: PropTypes.array,
  activeReportReviewNote: PropTypes.object,
  addNote: PropTypes.func,
  currentResult: PropTypes.object,
  datasheetCurrentVersionId: PropTypes.number,
  datasheetId: PropTypes.number,
  datasheetResultFamily: PropTypes.object,
  dispatchSaveStatus: PropTypes.func,
  factory: PropTypes.array,
  getResult: PropTypes.func,
  getResultAttachments: PropTypes.func,
  getResultExternalAttachments: PropTypes.func,
  hasSaqService: PropTypes.bool,
  hasUnverifiedSaqService: PropTypes.bool,
  isBlockSaveFailed: PropTypes.func,
  isCreateNote: PropTypes.bool,
  isSidebarExpanded: PropTypes.bool,
  isUnderReviewing: PropTypes.bool,
  jobId: PropTypes.number,
  onDeletingAttachment: PropTypes.func,
  onDeletingExternalAttachment: PropTypes.func,
  readonly: PropTypes.bool,
  refetchReportReview: PropTypes.func,
  resultHistories: PropTypes.array,
  selectedQuestion: PropTypes.object,
  setIsCreateNote: PropTypes.func,
  setResult: PropTypes.func,
  setShowHistory: PropTypes.func,
  setShowNote: PropTypes.func,
  setShowResult: PropTypes.func,
  showHistory: PropTypes.bool,
  showNote: PropTypes.bool,
  showResult: PropTypes.bool,
};

export const ResultVerificationSectionPropTypes = {
  ...ResultFamilySectionPropTypes,
}

export const QuestionAutofillPropTypes = {
  flash: PropTypes.bool,
  isRectifiedQuestion: PropTypes.bool,
  onChange: PropTypes.func,
  readonly: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  value: PropTypes.shape({
    rectifiedValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    type: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
};

export const QuestionCheckboxPropTypes = {
  errors: PropTypes.arrayOf(PropTypes.string),
  flash: PropTypes.bool,
  inTable: PropTypes.bool,
  isRectifiedQuestion: PropTypes.bool,
  label: PropTypes.node,
  onChange: PropTypes.func,
  value: PropTypes.shape({
    rectifiedValue: PropTypes.bool,
    type: PropTypes.string,
    value: PropTypes.bool,
  }),
};

export const QuestionCurrencyPropTypes = {
  isRectifiedQuestion: PropTypes.bool,
  onChange: PropTypes.func,
  question: PropTypes.shape({
    currencySettings: {
      currencyItems: {
        id: PropTypes.number,
        symbol: PropTypes.string,
      },
    },
  }),
  value: PropTypes.shape({
    currencyId: PropTypes.number,
    rectifiedValue: PropTypes.number,
    type: PropTypes.string,
    value: PropTypes.number,
  }),
};

export const QuestionDatePropTypes = {
  errors: PropTypes.arrayOf(PropTypes.string),
  flash: PropTypes.bool,
  isRectifiedQuestion: PropTypes.bool,
  onChange: PropTypes.func,
  value: PropTypes.shape({
    rectifiedValue: PropTypes.string,
    type: PropTypes.string,
    value: PropTypes.string,
  }),
};

export const QuestionDropdownPropTypes = {
  errors: PropTypes.arrayOf(PropTypes.string),
  flash: PropTypes.bool,
  isRectifiedQuestion: PropTypes.bool,
  onChange: PropTypes.func,
  question: PropTypes.shape({
    dropdownSettings: {
      allowMultiSelect: PropTypes.bool,
      options: PropTypes.arrayOf(PropTypes.string),
    },
    translations: PropTypes.array,
  }),
  value: PropTypes.shape({
    type: PropTypes.string,
    rectifiedValue: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string),
    ]),
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string),
    ]),
  }),
};

export const QuestionDependentDropdownPropTypes = {
  errors: PropTypes.arrayOf(PropTypes.string),
  flash: PropTypes.bool,
  isRectifiedQuestion: PropTypes.bool,
  onChange: PropTypes.func,
  question: PropTypes.shape({
    dependentDropdownSettings: {
      dependsOn: PropTypes.number,
      allowMultiSelect: PropTypes.bool,
      options: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.string,
        parent: PropTypes.string,
      })),
    },
    translations: PropTypes.array,
  }),
  value: PropTypes.shape({
    type: PropTypes.string,
    rectifiedValue: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string),
    ]),
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string),
    ]),
  }),
};

export const QuestionFileComponentPropTypes = {
  isRectifiedQuestion: PropTypes.bool,
  onChange: PropTypes.func,
  question: PropTypes.shape({
    fileSettings: PropTypes.shape({
      fileSize: PropTypes.number,
    }),
  }),
  readonly: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  value: PropTypes.shape({
    rectifiedValue: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    type: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  }),
};

export const QuestionMonthPropTypes = {
  errors: PropTypes.arrayOf(PropTypes.string),
  isRectifiedQuestion: PropTypes.bool,
  onChange: PropTypes.func,
  value: PropTypes.shape({
    rectifiedValue: PropTypes.string,
    type: PropTypes.string,
    value: PropTypes.string,
  }),
};

export const QuestionNumberPropTypes = {
  errors: PropTypes.arrayOf(PropTypes.string),
  flash: PropTypes.bool,
  isRectifiedQuestion: PropTypes.bool,
  onChange: PropTypes.func,
  question: PropTypes.shape({
    numberSettings: {
      precision: PropTypes.number,
    },
  }),
  readonly: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  value: PropTypes.shape({
    rectifiedValue: PropTypes.number,
    type: PropTypes.string,
    value: PropTypes.number,
  }),
};

export const QuestionPercentPropTypes = {
  errors: PropTypes.arrayOf(PropTypes.string),
  flash: PropTypes.bool,
  isRectifiedQuestion: PropTypes.bool,
  onChange: PropTypes.func,
  question: PropTypes.shape({
    numberSettings: {
      precision: PropTypes.number,
    },
  }),
  readonly: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  value: PropTypes.shape({
    rectifiedValue: PropTypes.number,
    type: PropTypes.string,
    value: PropTypes.number,
  }),
};

export const QuestionWithAttachmentsPropTypes = {
  errors: PropTypes.arrayOf(PropTypes.string),
  isRectifiedQuestion: PropTypes.bool,
  onChange: PropTypes.func,
  label: PropTypes.string,
  question: PropTypes.shape({
    pictureSettings: PropTypes.shape({
      allowMultiSelect: PropTypes.bool,
      options: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.string,
        fileId: PropTypes.number,
        fileUrl: PropTypes.string,
      })),
    }),
    voiceSettings: PropTypes.shape({
      allowMultiSelect: PropTypes.bool,
      options: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.string,
        fileId: PropTypes.number,
      })),
    }),
    questionType: PropTypes.string,
    translations: PropTypes.array,
  }),
  value: PropTypes.shape({
    type: PropTypes.string,
    rectifiedValue: PropTypes.oneOfType([
      PropTypes.int,
      PropTypes.arrayOf(PropTypes.int),
    ]),
    value: PropTypes.oneOfType([
      PropTypes.int,
      PropTypes.arrayOf(PropTypes.int),
    ]),
  }),
};

export const QuestionRelationPropTypes = {
  flash: PropTypes.bool,
  isRectifiedQuestion: PropTypes.bool,
  onChange: PropTypes.func,
  question: PropTypes.shape({
    relationSettings: {
      allowMultiSelect: PropTypes.bool,
      entityItems: PropTypes.arrayOf(PropTypes.string),
    },
  }),
  value: PropTypes.shape({
    rectifiedValue: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string),
    ]),
    type: PropTypes.string,
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string),
    ]),
  }),
};

export const QuestionSmileyRatingPropTypes = {
  isRectifiedQuestion: PropTypes.bool,
  onChange: PropTypes.func,
  question: PropTypes.shape({
    smileyRatingSettings: {
      available: PropTypes.arrayOf(PropTypes.bool),
      text: PropTypes.arrayOf(PropTypes.string),
    },
    translations: PropTypes.array,
  }),
  value: PropTypes.shape({
    type: PropTypes.string,
    rectifiedValue: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string),
    ]),
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string),
    ]),
  }),
};

export const QuestionStarRatingPropTypes = {
  isRectifiedQuestion: PropTypes.bool,
  onChange: PropTypes.func,
  question: PropTypes.shape({
    starRatingSettings: {
      numberOfStarsAvailable: PropTypes.int,
      rows: PropTypes.arrayOf(PropTypes.int),
    },
    translations: PropTypes.array,
  }),
  value: PropTypes.shape({
    type: PropTypes.string,
    rectifiedValue: PropTypes.oneOfType([
      PropTypes.int,
      PropTypes.arrayOf(PropTypes.int),
    ]),
    value: PropTypes.oneOfType([
      PropTypes.int,
      PropTypes.arrayOf(PropTypes.int),
    ]),
  }),
};

export const QuestionTablePropTypes = {
  errors: PropTypes.arrayOf(PropTypes.string),
  isAdditionalReportingDocuments: PropTypes.bool,
  isRectifiedQuestion: PropTypes.bool,
  job: PropTypes.object,
  onChange: PropTypes.func,
  question: PropTypes.shape({
    tableSettings: {
      columns: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
          type: PropTypes.string,
        }),
      ),
      enableMultipleRows: PropTypes.bool,
      fixedValues: PropTypes.arrayOf(
        PropTypes.shape({
          columnName: PropTypes.string,
          value: PropTypes.string,
        }),
      ),
    },
    translations: PropTypes.array,
  }),
  readonly: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  value: PropTypes.shape({
    rows: PropTypes.arrayOf(
      PropTypes.arrayOf(
        PropTypes.shape({
          column: PropTypes.string,
          rectifiedValue: PropTypes.any,
          type: PropTypes.string,
          value: PropTypes.any,
        }),
      ),
    ),
    type: PropTypes.string,
  }),
};

export const QuestionTextPropTypes = {
  errors: PropTypes.arrayOf(PropTypes.string),
  flash: PropTypes.bool,
  isRectifiedQuestion: PropTypes.bool,
  onChange: PropTypes.func,
  question: PropTypes.shape({
    textSettings: {
      isMaximized: PropTypes.bool,
      isTextarea: PropTypes.bool,
    },
  }),
  readonly: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  value: PropTypes.shape({
    rectifiedValue: PropTypes.string,
    type: PropTypes.string,
    value: PropTypes.string,
  }),
};

export const QuestionTimePropTypes = {
  isRectifiedQuestion: PropTypes.bool,
  onChange: PropTypes.func,
  readonly: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  value: PropTypes.shape({
    rectifiedValue: PropTypes.string,
    type: PropTypes.string,
    value: PropTypes.string,
  }),
};

export const QuestionYearPropTypes = {
  errors: PropTypes.arrayOf(PropTypes.string),
  isRectifiedQuestion: PropTypes.bool,
  onChange: PropTypes.func,
  value: PropTypes.shape({
    rectifiedValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    type: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
};

export const ExternalUserRightSideSectionPropTypes = {
  activeQuestion: PropTypes.object,
  activeReportReviewComments: PropTypes.array,
  activeReportReviewNote: PropTypes.object,
  datasheetCurrentVersionId: PropTypes.number,
  datasheetId: PropTypes.number,
  getResult: PropTypes.func,
  getResultAttachments: PropTypes.func,
  getResultExternalAttachments: PropTypes.func,
  hasOpenNoteForExternalUser: PropTypes.bool,
  hasSaqService: PropTypes.bool,
  hasUnverifiedSaqService: PropTypes.bool,
  isInfoCollapsed: PropTypes.bool,
  isReportReviewer: PropTypes.bool,
  isSidebarExpanded: PropTypes.bool,
  isUnderReviewing: PropTypes.bool,
  job: jobPropTypes,
  onDeletingExternalAttachment: PropTypes.func,
  readonly: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  refetchReportReview: PropTypes.func,
  setIsInfoCollapsed: PropTypes.func,
  setResult:PropTypes.func,
  setShowHistory: PropTypes.func,
  setShowNote: PropTypes.func,
  setShowResult: PropTypes.func,
  showNote: PropTypes.bool,
  showResult: PropTypes.bool,
  userIsAuthorizedExternalSaqUser: PropTypes.bool,
};

/* Default Props */

export const FormSectionDefaultProps = {
  indexPath: [],
  isQuesPresentInMultipleDatasheets: noop
};

export const QuestionAutofillDefaultProps = {
  isRectifiedQuestion: false,
};

export const QuestionCheckboxDefaultProps = {
  inTable: false,
  isRectifiedQuestion: false,
};

export const QuestionCurrencyDefaultProps = {
  isRectifiedQuestion: false,
};

export const QuestionDateDefaultProps = {
  isRectifiedQuestion: false,
};

export const QuestionDropdownDefaultProps = {
  isRectifiedQuestion: false,
};

export const QuestionDependentDropdownDefaultProps = {
  isRectifiedQuestion: false,
};

export const QuestionFileComponentDefaultProps = {
  isRectifiedQuestion: false,
};

export const QuestionMonthDefaultProps = {
  isRectifiedQuestion: false,
};

export const QuestionNumberDefaultProps = {
  isRectifiedQuestion: false,
};

export const QuestionPercentDefaultProps = {
  isRectifiedQuestion: false,
};

export const QuestionRelationDefaultProps = {
  isRectifiedQuestion: false,
};

export const QuestionSmileyRatingDefaultProps = {
  isRectifiedQuestion: false,
};

export const QuestionStarRatingDefaultProps = {
  isRectifiedQuestion: false,
};

export const QuestionTableDefaultProps = {
  isRectifiedQuestion: false,
};

export const QuestionTextDefaultProps = {
  isRectifiedQuestion: false,
};

export const QuestionTimeDefaultProps = {
  isRectifiedQuestion: false,
};

export const QuestionYearDefaultProps = {
  isRectifiedQuestion: false,
};

export const ExpandableTabsDefaultProps = {
  disableReviewNotesButton : false
};
